import React, { useState, useEffect } from 'react';
import { LogIn, User, Lock, AlertCircle, KeyRound, CheckCircle } from 'lucide-react';
import './Login.css';
import { useAuth } from '../hooks/useAuth';
import { useAuthStore } from '../stores/authStore';
import { authService } from '../services/authService';

interface LoginProps {
    onLoginSuccess: (userData: any) => void;
}

type ResetStep = 'none' | 'request' | 'confirm';

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);

    // 找回密码流程
    const [resetStep, setResetStep] = useState<ResetStep>('none');
    const [resetCode, setResetCode] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmNewPassword, setConfirmNewPassword] = useState<string>('');
    const [resetInfo, setResetInfo] = useState<string>('');

    const { login, demoLogin } = useAuth();

    // 2026-09-18 用户令"登录过就别再录"：记住上次用户名（只记名不记密码）
    useEffect(() => {
        const last = localStorage.getItem('last_username');
        if (last) setUsername(last);
    }, []);

    // 显示被动登出原因（会话过期 / 闲置超时等），读取后即清除
    useEffect(() => {
        const reason = useAuthStore.getState().logoutReason;
        if (reason) {
            setError(reason);
            useAuthStore.getState().clearLogoutReason();
        }
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!username.trim() || !password.trim()) {
            setError('请输入用户名和密码');
            return;
        }

        setIsLoading(true);

        try {
            console.log('🚀 开始登录流程...');
            const result = await login({ username, password });

            console.log('📋 登录结果:', result);

            if (result.success) {
                console.log('✅ 登录成功，触发回调');
                localStorage.setItem('last_username', username.trim());  // 记住账号（0918 用户令）
                // 登录成功，useAuth hook 会自动更新状态
                // onLoginSuccess 回调保持兼容性
                onLoginSuccess({ username, authString: btoa(`${username}:${password}`) });
            } else {
                setError(result.error || '登录失败');
            }
        } catch (err) {
            console.error('❌ 登录失败:', err);
            setError('网络连接失败，请检查后端服务是否运行');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDemoLogin = () => {
        demoLogin();
        onLoginSuccess({ username: 'demo', authString: btoa('demo:demo123') });
    };

    // ================= 找回密码 =================

    const startReset = () => {
        setError('');
        setResetInfo('');
        setResetCode('');
        setNewPassword('');
        setConfirmNewPassword('');
        setResetStep('request');
    };

    const cancelReset = () => {
        setResetStep('none');
        setError('');
        setResetInfo('');
    };

    const handleResetRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setResetInfo('');

        if (!username.trim()) {
            setError('请输入用户名');
            return;
        }

        setIsLoading(true);
        try {
            const result = await authService.requestPasswordReset(username.trim());
            setResetInfo(result.message + (result.dev_code ? `（开发模式验证码：${result.dev_code}）` : ''));
            setResetStep('confirm');
        } catch (err) {
            setError(err instanceof Error ? err.message : '请求失败，请稍后重试');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetConfirm = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setResetInfo('');

        if (!resetCode.trim()) {
            setError('请输入验证码');
            return;
        }
        if (newPassword.length < 6) {
            setError('新密码长度至少为 6 位');
            return;
        }
        if (/^\d+$/.test(newPassword)) {
            setError('新密码不能为纯数字');
            return;
        }
        if (newPassword !== confirmNewPassword) {
            setError('两次输入的新密码不一致');
            return;
        }

        setIsLoading(true);
        try {
            await authService.confirmPasswordReset(username.trim(), resetCode.trim(), newPassword);
            setResetInfo('密码重置成功，请使用新密码登录');
            setPassword('');
            setResetStep('none');
        } catch (err) {
            setError(err instanceof Error ? err.message : '重置失败，请稍后重试');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <div className="login-logo">
                        <LogIn size={48} />
                    </div>
                    <h1>NTRK审核助手</h1>
                    <p className="login-subtitle">NTRK审核服务系统</p>
                </div>

                {resetStep === 'none' ? (
                <form onSubmit={handleSubmit} className="login-form">
                    {error && (
                        <div className="error-message">
                            <AlertCircle size={16} />
                            <span>{error}</span>
                        </div>
                    )}
                    {resetInfo && (
                        <div className="error-message" style={{ backgroundColor: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d' }}>
                            <CheckCircle size={16} />
                            <span>{resetInfo}</span>
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="username">
                            <User size={18} />
                            用户名
                        </label>
                        <input
                            id="username"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="请输入用户名"
                            disabled={isLoading}
                            autoComplete="username"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">
                            <Lock size={18} />
                            密码
                        </label>
                        <div className="password-input-wrapper">
                            <input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="请输入密码"
                                disabled={isLoading}
                                autoComplete="current-password"
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                tabIndex={-1}
                            >
                                {showPassword ? '隐藏' : '显示'}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="login-button"
                        disabled={isLoading}
                    >
                        {isLoading ? '登录中...' : '登录'}
                    </button>

                    <div style={{ textAlign: 'right', marginTop: '8px' }}>
                        <button
                            type="button"
                            onClick={startReset}
                            disabled={isLoading}
                            style={{
                                border: 'none', background: 'none', cursor: 'pointer',
                                color: '#1677ff', fontSize: '13px', padding: 0,
                            }}
                        >
                            忘记密码？
                        </button>
                    </div>

                    <div className="login-divider">
                        <span>或</span>
                    </div>

                    <button
                        type="button"
                        className="demo-button"
                        onClick={handleDemoLogin}
                        disabled={isLoading}
                    >
                        使用演示账号登录
                    </button>

                    <div className="login-footer">
                        <p className="login-hint">
                            本机登录后 30 天内免重登；连续 24 小时无操作将自动退出登录
                        </p>
                    </div>
                </form>
                ) : resetStep === 'request' ? (
                <form onSubmit={handleResetRequest} className="login-form">
                    {error && (
                        <div className="error-message">
                            <AlertCircle size={16} />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="reset-username">
                            <User size={18} />
                            用户名
                        </label>
                        <input
                            id="reset-username"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="请输入需要找回密码的用户名"
                            disabled={isLoading}
                            autoComplete="username"
                            required
                        />
                    </div>

                    <button type="submit" className="login-button" disabled={isLoading}>
                        {isLoading ? '发送中...' : '发送重置验证码'}
                    </button>

                    <div className="login-footer">
                        <p className="login-hint">
                            验证码将发送至账号绑定的邮箱，15 分钟内有效
                        </p>
                        <button
                            type="button"
                            onClick={cancelReset}
                            style={{
                                border: 'none', background: 'none', cursor: 'pointer',
                                color: '#1677ff', fontSize: '13px', padding: 0,
                            }}
                        >
                            返回登录
                        </button>
                    </div>
                </form>
                ) : (
                <form onSubmit={handleResetConfirm} className="login-form">
                    {error && (
                        <div className="error-message">
                            <AlertCircle size={16} />
                            <span>{error}</span>
                        </div>
                    )}
                    {resetInfo && (
                        <div className="error-message" style={{ backgroundColor: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d' }}>
                            <CheckCircle size={16} />
                            <span>{resetInfo}</span>
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="reset-code">
                            <KeyRound size={18} />
                            验证码
                        </label>
                        <input
                            id="reset-code"
                            type="text"
                            value={resetCode}
                            onChange={(e) => setResetCode(e.target.value)}
                            placeholder="请输入 6 位验证码"
                            disabled={isLoading}
                            maxLength={6}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="new-password">
                            <Lock size={18} />
                            新密码
                        </label>
                        <input
                            id="new-password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="至少 6 位，不能为纯数字"
                            disabled={isLoading}
                            autoComplete="new-password"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirm-new-password">
                            <Lock size={18} />
                            确认新密码
                        </label>
                        <input
                            id="confirm-new-password"
                            type="password"
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            placeholder="请再次输入新密码"
                            disabled={isLoading}
                            autoComplete="new-password"
                            required
                        />
                    </div>

                    <button type="submit" className="login-button" disabled={isLoading}>
                        {isLoading ? '重置中...' : '重置密码'}
                    </button>

                    <div className="login-footer">
                        <p className="login-hint">
                            未收到验证码？请检查垃圾邮件，或联系管理员重置
                        </p>
                        <button
                            type="button"
                            onClick={cancelReset}
                            style={{
                                border: 'none', background: 'none', cursor: 'pointer',
                                color: '#1677ff', fontSize: '13px', padding: 0,
                            }}
                        >
                            返回登录
                        </button>
                    </div>
                </form>
                )}

                <div className="login-features">
                    <h3>系统功能</h3>
                    <ul>                       
                        <li>文件上传与分析</li>
                        <li>聊天历史记录</li>                       
                    </ul>
                </div>

                <div className="login-icp-footer">
                    <a
                        href="https://beian.miit.gov.cn/"
                        target="_blank"
                        rel="noreferrer"
                    >
                        沪ICP备2026042261号
                    </a>
                </div>
            </div>
        </div>
    );
};

export default Login;